import { useEffect, useState } from "react";

const CONSENT_KEY = "airobotcam_cookie_consent";
const OPEN_EVENT = "airobotcam:open-cookie-preferences";

export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function loadAnalytics() {
  if (document.getElementById("air-analytics-script")) return;
  const s = document.createElement("script");
  s.id = "air-analytics-script";
  s.defer = true;
  s.src = "/analytics.js";
  document.head.appendChild(s);
}

function Toggle({ checked, onChange, disabled, testid, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-testid={testid}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full border transition-colors duration-300 ${
        checked ? "border-cyan-300/50 bg-cyan-300/25" : "border-white/15 bg-white/5"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full transition-all duration-300 ${
          checked ? "left-[22px] bg-cyan-300" : "left-[3px] bg-white/40"
        }`}
      />
    </button>
  );
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (c) {
      if (c.analytics) loadAnalytics();
    } else {
      setVisible(true);
    }
    const onOpen = () => {
      const cur = readConsent();
      setAnalytics(Boolean(cur && cur.analytics));
      setManage(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const save = (allow) => {
    try {
      localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ necessary: true, analytics: allow, ts: new Date().toISOString() })
      );
    } catch {}
    if (allow) loadAnalytics();
    window.dispatchEvent(new Event("airobotcam:consent-updated"));
    setVisible(false);
    setManage(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent"
      role="dialog"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[80] px-2.5 pb-2.5 sm:px-6 sm:pb-6"
    >
      <div className="mx-auto max-w-xl rounded-2xl border border-white/10 bg-[#0a0a0a]/95 p-3.5 shadow-[0_20px_80px_rgba(0,0,0,0.65)] backdrop-blur-2xl sm:p-5">
        <p className="font-display hidden text-[10px] font-semibold uppercase tracking-[0.3em] text-cyan-300/90 min-[420px]:block">
          Your Privacy
        </p>

        {!manage ? (
          <>
            <p className="mt-1.5 text-[11px] leading-snug text-[#C5CBD5] sm:text-sm sm:leading-relaxed">
              Optional, privacy-friendly analytics help us understand how this site is used — no
              advertising trackers. Details in our{" "}
              <a
                href="/cookie-policy"
                data-testid="cookie-policy-link"
                className="text-cyan-200/70 underline-offset-4 transition-colors hover:text-cyan-100 hover:underline"
              >
                Cookie Policy
              </a>
              ; change anytime via Cookie Preferences in the footer.
            </p>
            <div className="mt-3.5 flex items-center gap-2.5">
              <button
                data-testid="cookie-accept"
                onClick={() => save(true)}
                className="flex-1 rounded-md bg-cyan-300 px-4 py-2 text-xs font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98] sm:flex-none sm:px-6 sm:py-3 sm:text-sm"
              >
                Accept
              </button>
              <button
                data-testid="cookie-reject"
                onClick={() => save(false)}
                className="flex-1 rounded-md border border-cyan-300/30 px-4 py-2 text-xs font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:text-cyan-100 active:scale-[0.98] sm:flex-none sm:px-6 sm:py-3 sm:text-sm"
              >
                Reject
              </button>
              <button
                data-testid="cookie-manage"
                onClick={() => setManage(true)}
                className="shrink-0 px-1 py-2 text-xs font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100 sm:ml-auto sm:py-3 sm:text-sm"
              >
                Manage
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="mt-5 space-y-3">
              <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
                <div>
                  <p className="text-sm font-medium text-white/85">Strictly Necessary</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/70">
                    Stores your consent choice in your browser so we can honour it. Always active.
                  </p>
                </div>
                <Toggle checked disabled testid="cookie-toggle-necessary" label="Strictly necessary storage — always active" onChange={() => {}} />
              </div>
              <div className="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.03] px-5 py-4">
                <div>
                  <p className="text-sm font-medium text-white/85">Analytics</p>
                  <p className="mt-1 text-xs leading-relaxed text-white/70">
                    Optional cookieless analytics: page views, referrer and device type. No cookies,
                    no cross-site tracking.
                  </p>
                </div>
                <Toggle checked={analytics} onChange={setAnalytics} testid="cookie-toggle-analytics" label="Optional analytics" />
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <button
                data-testid="cookie-save"
                onClick={() => save(analytics)}
                className="rounded-md bg-cyan-300 px-6 py-3 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98]"
              >
                Save Preferences
              </button>
              <button
                data-testid="cookie-manage-back"
                onClick={() => setManage(false)}
                className="px-2 py-3 text-sm font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100 sm:ml-auto"
              >
                Back
              </button>
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-white/55">
              Full details in our{" "}
              <a
                href="/cookie-policy"
                data-testid="cookie-policy-link-manage"
                className="text-cyan-200/70 underline-offset-4 transition-colors hover:text-cyan-100 hover:underline"
              >
                Cookie Policy
              </a>
              .
            </p>
          </>
        )}
      </div>
    </div>
  );
}
